import React, { useState } from "react";
import { useTranslate } from "@refinedev/core";
import { Link, useSearchParams } from "react-router-dom";
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Layout,
  Result,
  Row,
  Spin,
  notification,
  theme,
} from "antd";
import { axiosInstance } from "../../authProvider";
import { PageTitle } from "../login/components/PageTitle";
import { AuthHeroPanel } from "../login/components/AuthHeroPanel";

const { useToken } = theme;

const AcceptInvitation = () => {
  const _t = useTranslate();
  const { token } = useToken();
  const [form] = Form.useForm();
  const [params] = useSearchParams();
  const invitationToken = params.get("token");
  const [loading, setLoading] = useState(false);
  const [outcome, setOutcome] = useState<
    "form" | "accepted" | "alreadyAccepted" | "invalidToken" | "tokenExpired"
  >(invitationToken ? "form" : "invalidToken");
  const [notif, contextHolder] = notification.useNotification();

  const onFinish = async (values: {
    firstName: string;
    lastName: string;
    password: string;
  }) => {
    if (!invitationToken) {
      setOutcome("invalidToken");
      return;
    }
    setLoading(true);
    try {
      const response = await axiosInstance.post("/users/accept-invitation", {
        token: invitationToken,
        firstName: values.firstName,
        lastName: values.lastName,
        password: values.password,
      });
      const status = response?.data?.status;
      setOutcome(status === "alreadyAccepted" ? "alreadyAccepted" : "accepted");
    } catch (error: any) {
      const tokenError = error?.response?.data?.errors?.token;
      if (tokenError === "tokenExpired") {
        setOutcome("tokenExpired");
      } else if (tokenError === "invalidToken") {
        setOutcome("invalidToken");
      } else {
        notif.error({
          message: _t("pages.register.errors.title"),
          description: _t("pages.register.errors.unknown"),
        });
      }
    } finally {
      setLoading(false);
    }
  };

  if (outcome !== "form") {
    const isSuccess = outcome === "accepted" || outcome === "alreadyAccepted";
    const title =
      outcome === "accepted"
        ? _t("pages.acceptInvitation.successMessage")
        : outcome === "alreadyAccepted"
          ? _t("pages.acceptInvitation.alreadyAcceptedMessage")
          : outcome === "tokenExpired"
            ? _t("pages.acceptInvitation.expiredTokenMessage")
            : _t("pages.acceptInvitation.invalidTokenMessage");
    const subTitle =
      isSuccess
        ? _t("pages.acceptInvitation.successSubMessage")
        : undefined;

    return (
      <Row
        justify="center"
        align="middle"
        style={{ minHeight: "100vh", backgroundColor: token.colorBgLayout }}
      >
        <Col className="sm:w-100 md:w-1/2">
          <Card
            className="mt-16 text-center"
            style={{
              backgroundColor: token.colorBgContainer,
              borderColor: token.colorBorder,
            }}
          >
            <Result
              status={isSuccess ? "success" : "error"}
              title={title}
              subTitle={subTitle}
              extra={
                <Link to="/login">{_t("pages.register.gotoLoginMessage")}</Link>
              }
            />
          </Card>
        </Col>
      </Row>
    );
  }

  return (
    <>
      {contextHolder}
      <Layout style={{ backgroundColor: token.colorBgLayout }}>
        <Row style={{ minHeight: "100dvh" }}>
          <Col
            xs={24}
            lg={10}
            className="overflow-y-auto"
            style={{
              padding: "16px",
              paddingTop: "128px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
              alignItems: "center",
              backgroundColor: token.colorBgContainer,
            }}
          >
            <div className="w-full py-4" style={{ maxWidth: 600 }}>
              <PageTitle />
              <Card
                title={_t("pages.acceptInvitation.title")}
                className="mt-8"
                style={{
                  backgroundColor: token.colorBgContainer,
                  borderColor: token.colorBorder,
                  color: token.colorText,
                }}
              >
                <p style={{ color: token.colorTextSecondary }}>
                  {_t("pages.acceptInvitation.helpMessage")}
                </p>
                <Form
                  form={form}
                  name="accept-invitation"
                  layout="vertical"
                  onFinish={onFinish}
                  autoComplete="off"
                >
                  <Form.Item
                    name="firstName"
                    label={_t("users.fields.firstName")}
                    rules={[
                      {
                        required: true,
                        message: _t("pages.register.errors.required"),
                      },
                    ]}
                  >
                    <Input size="large" />
                  </Form.Item>
                  <Form.Item
                    name="lastName"
                    label={_t("users.fields.lastName")}
                    rules={[
                      {
                        required: true,
                        message: _t("pages.register.errors.required"),
                      },
                    ]}
                  >
                    <Input size="large" />
                  </Form.Item>
                  <Form.Item
                    name="password"
                    label={_t("users.fields.password")}
                    rules={[
                      {
                        required: true,
                        message: _t("pages.register.errors.required"),
                      },
                      {
                        min: 6,
                        message: _t("fields.errors.minLength", { min: 6 }),
                      },
                    ]}
                  >
                    <Input.Password size="large" />
                  </Form.Item>
                  <Form.Item
                    name="confirmPassword"
                    dependencies={["password"]}
                    label={_t("users.fields.confirmPassword")}
                    rules={[
                      {
                        required: true,
                        message: _t("pages.register.errors.required"),
                      },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (!value || getFieldValue("password") === value) {
                            return Promise.resolve();
                          }
                          return Promise.reject(
                            _t("pages.register.errors.passwordMismatch"),
                          );
                        },
                      }),
                    ]}
                  >
                    <Input.Password size="large" />
                  </Form.Item>
                  <Form.Item>
                    <Spin spinning={loading}>
                      <Button
                        type="primary"
                        htmlType="submit"
                        size="large"
                        block
                      >
                        {_t("pages.acceptInvitation.submit")}
                      </Button>
                    </Spin>
                  </Form.Item>
                  <div className="text-center">
                    <Link to="/login">
                      {_t("pages.register.gotoLoginMessage")}
                    </Link>
                  </div>
                </Form>
              </Card>
            </div>
          </Col>
          <Col xs={0} lg={14} className="relative min-h-full p-0">
            <AuthHeroPanel />
          </Col>
        </Row>
      </Layout>
    </>
  );
};

export default AcceptInvitation;
